'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, SlidersHorizontal, Clock, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';
import SearchResults from './SearchResults';
import AppImage from '@/components/ui/AppImage';

// ─── Synonym Map ──────────────────────────────────────────────────────────────
const synonymMap: Record<string, string[]> = {
  'job seeker': ['candidate', 'applicant', 'worker looking for job', 'job hunter'],
  'candidate': ['job seeker', 'applicant', 'worker'],
  'ex-uk': ['imported used', 'second hand uk', 'used from uk', 'ex uk'],
  'imported used': ['ex-uk', 'ex-japan', 'ex-china', 'second hand imported'],
  'vehicle': ['car', 'automobile', 'motor vehicle', 'auto'],
  'car': ['vehicle', 'automobile', 'auto', 'motor'],
  'spares': ['spare parts', 'auto parts', 'vehicle parts', 'car parts'],
  'spare parts': ['spares', 'auto parts', 'vehicle parts', 'parts'],
  'laptop': ['computer', 'notebook', 'pc', 'portable computer'],
  'computer': ['laptop', 'desktop', 'pc', 'notebook'],
  'house': ['property', 'home', 'residence', 'dwelling'],
  'property': ['house', 'apartment', 'flat', 'home', 'real estate'],
  'second-hand': ['used', 'pre-owned', 'ex-uk', 'imported used'],
  'used': ['second-hand', 'pre-owned', 'refurbished'],
  'motorbike': ['motorcycle', 'boda boda', 'bike', 'two-wheeler'],
  'motorcycle': ['motorbike', 'boda boda', 'bike'],
  'phone': ['mobile', 'smartphone', 'handset', 'cellphone'],
  'mobile': ['phone', 'smartphone', 'handset'],
};

// ─── Related Searches ─────────────────────────────────────────────────────────
const relatedSearchesMap: Record<string, string[]> = {
  'jobs': ['Jobs in Nairobi', 'Overseas jobs for Kenyans', 'Driver jobs Kuwait', 'Security guard jobs Qatar', 'Housemaid jobs Oman'],
  'cars': ['Toyota Harrier Kenya', 'Ex-Japan cars Nairobi', 'Used cars under 1M', 'Toyota Vitz for sale', 'Nissan Note Kenya'],
  'property': ['Apartments for rent Nairobi', 'Bedsitters Kasarani', 'Houses for sale Kiambu', 'Land for sale Kenya'],
  'phones': ['iPhone 15 Pro Nairobi', 'Samsung S24 Kenya', 'Used phones Nairobi', 'Ex-UK phones Kenya'],
  'spare parts': ['Toyota spare parts Nairobi', 'Nissan parts Kenya', 'Engine parts Nairobi', 'Gearbox Kenya'],
  'laptops': ['HP EliteBook Kenya', 'Dell laptops Nairobi', 'Used laptops Kenya', 'Gaming laptops Nairobi'],
  'fashion': ['Women clothes Kenya', 'Ex-UK clothes Nairobi', 'African wear Kenya', 'Shoes for sale Nairobi'],
};

// ─── Category Suggestions ─────────────────────────────────────────────────────
const categorySuggestions = [
  { id: 'cs-jobs', label: 'Jobs', icon: '💼', color: '#FFB300', module: 'jobs' },
  { id: 'cs-classifieds', label: 'Classifieds', icon: '🏷️', color: '#00BCD4', module: 'classifieds' },
  { id: 'cs-cars', label: 'Cars & Vehicles', icon: '🚗', color: '#FF6D00', module: 'vehicles' },
  { id: 'cs-property', label: 'Property', icon: '🏠', color: '#E040FB', module: 'property' },
  { id: 'cs-phones', label: 'Phones', icon: '📱', color: '#00C853', module: 'classifieds' },
  { id: 'cs-spares', label: 'Spare Parts', icon: '🔧', color: '#FF3B5C', module: 'spare-parts' },
  { id: 'cs-laptops', label: 'Laptops', icon: '💻', color: '#7C4DFF', module: 'computers' },
  { id: 'cs-fashion', label: 'Fashion', icon: '👗', color: '#FF4081', module: 'beauty-fashion' },
  { id: 'cs-hotels', label: 'Hotels', icon: '🏨', color: '#00BCD4', module: 'hotels' },
  { id: 'cs-services', label: 'Services', icon: '🛠️', color: '#FF9800', module: 'services' },
];

const recentSearches = ['iPhone 15', 'Software Engineer Nairobi', 'Toyota Corolla', '2BR apartment Kilimani', 'Plumber Karen'];
const trendingSearches = ['Land Cruiser 2020', 'Accounting Jobs', 'Samsung S24', 'Furnished Apartment', 'Graphic Designer'];

const categoryFilters = [
  { id: 'cat-all', label: 'All', color: '#00C853' },
  { id: 'cat-classifieds', label: 'Classifieds', color: '#00BCD4' },
  { id: 'cat-jobs', label: 'Jobs', color: '#FFB300' },
  { id: 'cat-property', label: 'Property', color: '#E040FB' },
  { id: 'cat-vehicles', label: 'Vehicles', color: '#FF6D00' },
  { id: 'cat-spare-parts', label: 'Spare Parts', color: '#FF3B5C' },
  { id: 'cat-services', label: 'Services', color: '#FF9800' },
  { id: 'cat-businesses', label: 'Businesses', color: '#7C4DFF' },
  { id: 'cat-people', label: 'People', color: '#FF4081' },
  { id: 'cat-events', label: 'Events', color: '#EA80FC' },
];

// ─── Typo Tolerance: simple fuzzy match ──────────────────────────────────────
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function getSynonyms(query: string): string[] {
  const lower = query.toLowerCase().trim();
  for (const [key, values] of Object.entries(synonymMap)) {
    if (lower.includes(key) || key.includes(lower)) {
      return values;
    }
  }
  return [];
}

function getRelatedSearches(query: string): string[] {
  const lower = query.toLowerCase().trim();
  for (const [key, values] of Object.entries(relatedSearchesMap)) {
    if (lower.includes(key) || key.includes(lower)) {
      return values;
    }
  }
  // fallback: return trending
  return trendingSearches.slice(0, 4);
}

function getTypoSuggestion(query: string): string | null {
  const allTerms = [...recentSearches, ...trendingSearches];
  let best: string | null = null;
  let bestDist = Infinity;
  for (const term of allTerms) {
    const dist = levenshtein(query.toLowerCase(), term.toLowerCase());
    if (dist > 0 && dist <= 3 && dist < bestDist) {
      bestDist = dist;
      best = term;
    }
  }
  return best;
}

export default function SearchScreenClient() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('cat-all');
  const [showResults, setShowResults] = useState(false);
  const [synonyms, setSynonyms] = useState<string[]>([]);
  const [relatedSearches, setRelatedSearches] = useState<string[]>([]);
  const [typoSuggestion, setTypoSuggestion] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const analyticsRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTrackedQuery = useRef('');

  // ─── Track search analytics ───────────────────────────────────────────────
  const trackSearch = useCallback(async (q: string, category: string, resultCount?: number, converted?: boolean, clickedItemId?: string) => {
    if (!q || q.length < 2) return;
    try {
      await fetch('/api/search-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: q,
          module: category === 'cat-all' ? 'all' : category.replace('cat-', ''),
          category: category === 'cat-all' ? null : category.replace('cat-', ''),
          resultCount: resultCount ?? -1,
          converted: converted ?? false,
          clickedItemId: clickedItemId || null,
        }),
      });
    } catch {
      // silently fail — analytics must never break search UX
    }
  }, []);

  const handleSearch = useCallback((q: string) => {
    setQuery(q);
    setShowResults(q.length > 1);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (q.length > 1) {
        setSynonyms(getSynonyms(q));
        setRelatedSearches(getRelatedSearches(q));
        setTypoSuggestion(getTypoSuggestion(q));
        setShowSuggestions(true);
      } else {
        setSynonyms([]);
        setRelatedSearches([]);
        setTypoSuggestion(null);
        setShowSuggestions(false);
      }
    }, 300);

    // Debounced analytics tracking — fire after user stops typing for 800ms
    if (analyticsRef.current) clearTimeout(analyticsRef.current);
    if (q.length > 1 && q !== lastTrackedQuery.current) {
      analyticsRef.current = setTimeout(() => {
        lastTrackedQuery.current = q;
        trackSearch(q, activeCategory);
      }, 800);
    }
  }, [activeCategory, trackSearch]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (analyticsRef.current) clearTimeout(analyticsRef.current);
    };
  }, []);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-4">
      {/* Search header */}
      <div className="sticky top-0 z-20 pb-4" style={{ background: 'var(--background)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-200"
            style={{ background: 'var(--input)', border: `2px solid ${showResults ? 'var(--primary)' : 'var(--border)'}` }}>
            <Search size={18} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search jobs, cars, spare parts, property..."
              className="flex-1 bg-transparent outline-none text-sm font-500 text-foreground placeholder-muted-foreground"
              autoFocus
            />
            {query && (
              <button onClick={() => { handleSearch(''); setShowSuggestions(false); }} className="transition-all duration-150 active:scale-90">
                <X size={16} style={{ color: 'var(--muted-foreground)' }} />
              </button>
            )}
          </div>
          <button className="p-3 rounded-2xl transition-all duration-150 hover:bg-muted active:scale-95"
            style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
            <SlidersHorizontal size={18} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
          {categoryFilters.map((cat) => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-600 transition-all duration-150 active:scale-95"
              style={{
                background: activeCategory === cat.id ? `${cat.color}20` : 'var(--secondary)',
                color: activeCategory === cat.id ? cat.color : 'var(--muted-foreground)',
                border: `1px solid ${activeCategory === cat.id ? cat.color : 'var(--border)'}`,
              }}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {showResults ? (
        <div className="flex flex-col gap-4">
          {/* Typo suggestion */}
          {typoSuggestion && typoSuggestion.toLowerCase() !== query.toLowerCase() && (
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
              style={{ background: 'rgba(255,179,0,0.08)', border: '1px solid rgba(255,179,0,0.2)' }}>
              <Sparkles size={14} style={{ color: 'var(--accent)' }} />
              <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Did you mean:</span>
              <button onClick={() => handleSearch(typoSuggestion)}
                className="text-sm font-700 underline" style={{ color: 'var(--accent)' }}>
                {typoSuggestion}
              </button>
            </div>
          )}

          {/* Synonyms / also search for */}
          {synonyms.length > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-700 uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
                Also search for
              </p>
              <div className="flex flex-wrap gap-2">
                {synonyms.map(syn => (
                  <button key={syn} onClick={() => handleSearch(syn)}
                    className="px-3 py-1.5 rounded-full text-xs font-600 transition-all active:scale-95"
                    style={{ background: 'rgba(0,200,83,0.08)', color: 'var(--primary)', border: '1px solid rgba(0,200,83,0.2)' }}>
                    {syn}
                  </button>
                ))}
              </div>
            </div>
          )}

          <SearchResults query={query} category={activeCategory} />

          {/* Related searches */}
          {relatedSearches.length > 0 && (
            <div className="mt-2 p-4 rounded-2xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-700 uppercase tracking-wider mb-3" style={{ color: 'var(--muted-foreground)' }}>
                Related Searches
              </p>
              <div className="flex flex-col gap-0">
                {relatedSearches.map(rel => (
                  <button key={rel} onClick={() => handleSearch(rel)}
                    className="flex items-center justify-between py-2.5 border-b last:border-b-0 text-left transition-all hover:opacity-70"
                    style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2">
                      <Search size={12} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-sm text-foreground">{rel}</span>
                    </div>
                    <ArrowRight size={12} style={{ color: 'var(--muted-foreground)' }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Category suggestions grid */}
          <div>
            <h3 className="text-sm font-700 text-foreground mb-3">Browse by Category</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {categorySuggestions.map(cat => (
                <button key={cat.id} onClick={() => handleSearch(cat.label)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition-all active:scale-95"
                  style={{ background: `${cat.color}10`, border: `1px solid ${cat.color}25` }}>
                  <span className="text-lg">{cat.icon}</span>
                  <span className="text-xs font-600" style={{ color: cat.color }}>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent searches */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} style={{ color: 'var(--muted-foreground)' }} />
              <h3 className="text-sm font-600 text-foreground">Recent Searches</h3>
            </div>
            <div className="flex flex-col gap-0 rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)', background: 'var(--card)' }}>
              {recentSearches.map((s) => (
                <button key={`recent-${s}`} onClick={() => handleSearch(s)}
                  className="flex items-center justify-between px-4 py-3.5 border-b last:border-b-0 transition-all duration-150 hover:bg-muted/50 text-left"
                  style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-3">
                    <Clock size={14} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-sm font-500 text-foreground">{s}</span>
                  </div>
                  <X size={13} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              ))}
            </div>
          </div>

          {/* Trending */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} style={{ color: 'var(--accent)' }} />
              <h3 className="text-sm font-600 text-foreground">Trending in Kenya</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {trendingSearches.map((s) => (
                <button key={`trending-${s}`} onClick={() => handleSearch(s)}
                  className="px-3 py-1.5 rounded-full text-xs font-600 transition-all duration-150 active:scale-95 hover:border-accent"
                  style={{ background: 'rgba(255,179,0,0.08)', color: 'var(--accent)', border: '1px solid rgba(255,179,0,0.2)' }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Synonym hints */}
          <div className="p-4 rounded-2xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} style={{ color: 'var(--primary)' }} />
              <h3 className="text-sm font-600 text-foreground">Smart Search Tips</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { tip: '"Job seeker" = "Candidate"', example: 'Try: candidate nairobi' },
                { tip: '"Ex-UK" = "Imported used"', example: 'Try: imported used phones' },
                { tip: '"Vehicle" = "Car"', example: 'Try: car for sale nairobi' },
                { tip: '"Spares" = "Spare parts"', example: 'Try: toyota spare parts' },
              ].map(item => (
                <div key={item.tip} className="flex flex-col gap-0.5">
                  <span className="text-xs font-600 text-foreground">{item.tip}</span>
                  <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{item.example}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Suggested categories with images */}
          <div>
            <h3 className="text-sm font-600 text-foreground mb-3">Featured Categories</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {[
                { id: 'browse-cars', label: 'Cars & Vehicles', count: '2,340 listings', img: 'https://images.unsplash.com/photo-1645198914534-3225c6a6b798', alt: 'Various cars in a parking lot in Nairobi' },
                { id: 'browse-phones', label: 'Phones', count: '1,890 listings', img: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a6bbc97c-1766486052960.png', alt: 'Assorted smartphones displayed on a shelf' },
                { id: 'browse-jobs', label: 'Jobs', count: '4,120 openings', img: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e4ca2751-1774197885680.png', alt: 'Business professionals in a meeting room' },
                { id: 'browse-property', label: 'Property', count: '980 listings', img: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ea8389d5-1769257683471.png', alt: 'Modern apartment building in Nairobi suburb' },
                { id: 'browse-fashion', label: 'Fashion', count: '3,450 items', img: 'https://img.rocket.new/generatedImages/rocket_gen_img_129f2f1d2-1772942194506.png', alt: 'Colourful clothing rack in a boutique store' },
                { id: 'browse-services', label: 'Services', count: '760 providers', img: 'https://img.rocket.new/generatedImages/rocket_gen_img_1af9edc25-1773125362026.png', alt: 'Technician repairing electronic equipment' },
              ].map((cat) => (
                <button key={cat.id} onClick={() => handleSearch(cat.label)}
                  className="listing-card overflow-hidden text-left">
                  <AppImage src={cat.img} alt={cat.alt} width={200} height={100}
                    className="w-full h-24 object-cover" />
                  <div className="p-2.5">
                    <p className="text-xs font-600 text-foreground">{cat.label}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--primary)' }}>{cat.count}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
